import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

type Mode = 'resources' | 'library';

type ItemKind = '' | 'Document' | 'Chat' | 'PDF';

interface ProjectsFilterPopoverProps {
  mode: Mode;
  ariaLabel: string;
  documentTypes: string[];
  categories: string[];
  /** Optional display labels for category/tag option values (slug → label). */
  categoryLabels?: Record<string, string>;
  filterType: string;
  onFilterTypeChange: (value: string) => void;
  filterCategory: string;
  onFilterCategoryChange: (value: string) => void;
  itemKind?: ItemKind;
  onItemKindChange?: (value: ItemKind) => void;
}

export function ProjectsFilterPopover({
  mode,
  ariaLabel,
  documentTypes,
  categories,
  categoryLabels,
  filterType,
  onFilterTypeChange,
  filterCategory,
  onFilterCategoryChange,
  itemKind,
  onItemKindChange,
}: ProjectsFilterPopoverProps) {
  const showItemType =
    mode === 'library' && typeof onItemKindChange === 'function';

  const showDocumentFilters =
    documentTypes.length > 0 || categories.length > 0;

  const showDocumentSections =
    showDocumentFilters &&
    (mode === 'resources' || !itemKind || itemKind === 'Document');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-9 font-medium text-foreground border-border bg-background hover:bg-accent/50"
          aria-label={ariaLabel}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filter
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Filters</h4>

          {showItemType && (
            <div className="space-y-2">
              <Label htmlFor="project-kind-select">Item type</Label>
              <Select
                value={itemKind || 'all'}
                onValueChange={(val) =>
                  onItemKindChange(val === 'all' ? '' : (val as ItemKind))
                }
              >
                <SelectTrigger id="project-kind-select" className="h-9">
                  <SelectValue placeholder="All item types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All item types</SelectItem>
                  <SelectItem value="Document">Document</SelectItem>
                  <SelectItem value="Chat">Chat</SelectItem>
                  <SelectItem value="PDF">PDF</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {showDocumentSections && documentTypes.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="projects-type-select">
                {mode === 'resources' ? 'Type' : 'Document type'}
              </Label>
              <Select
                value={filterType || 'all'}
                onValueChange={(val) =>
                  onFilterTypeChange(val === 'all' ? '' : val)
                }
              >
                <SelectTrigger id="projects-type-select" className="h-9">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {documentTypes.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {showDocumentSections && categories.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="projects-category-select">Tag</Label>
              <Select
                value={filterCategory || 'all'}
                onValueChange={(val) =>
                  onFilterCategoryChange(val === 'all' ? '' : val)
                }
              >
                <SelectTrigger id="projects-category-select" className="h-9">
                  <SelectValue placeholder="All tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tags</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {categoryLabels?.[c] ?? c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

